import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  getSettings,
  patchSettings,
  patchGameSettings,
  skipToRound,
  setTestingMode,
} from "@/lib/api";
import { weatherNextSpin } from "@/lib/api";
import { STAT_META } from "@/components/game/CardStats";

/**
 * SettingsPanel — host-only settings modal.
 *
 * Loads the unified settings tree from `/api/settings`, stages local
 * changes in `pending`, and writes them back via PATCH /settings (when
 * gameId is null) or POST /games/{id}/settings (when a game is
 * running). The backend rejects writes mid-turn with HTTP 409, which
 * we surface as a toast.
 *
 * Groups (v2, in order): Setup & economy · Turn · Deck · Card costs ·
 * Card stats · Board · Weather · Sea level rise · Scoring · Game end,
 * then Display (screen-only, not rules).
 *
 * For every numeric/boolean leaf we expose a typed input. List/dict
 * leaves render as JSON for advanced edits; the most-used dropdowns
 * (tiebreaker order, participating types) get bespoke widgets.
 */
// readOnly: online games view settings only (Testing tools stay for the leader via showTesting).
export default function SettingsPanel(props) {
  return (
    <SettingsErrorBoundary onClose={props.onClose}>
      <SettingsPanelInner {...props} />
    </SettingsErrorBoundary>
  );
}

// A bad row must never blank the whole screen: show a small message and a Close button instead.
class SettingsErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error) {
    // eslint-disable-next-line no-console
    console.error("SettingsPanel crashed", error);
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="settings-error">
        <div className="bg-white border border-[color:var(--line)] p-5 max-w-sm text-sm">
          <p className="font-bold mb-2">The settings panel hit a problem.</p>
          <p className="text-[color:var(--ink-3)] mb-3">Nothing was changed. Close it and carry on.</p>
          <button type="button" className="border border-[color:var(--line)] px-3 py-1" onClick={this.props.onClose}>Close</button>
        </div>
      </div>
    );
  }
}

function SettingsPanelInner({ gameId, gameTestingMode = false, gameForcedWeather = null, onClose, onApplied, readOnly = false, showTesting = true, onError }) {
  const [tree, setTree] = useState(null);
  const [pending, setPending] = useState({});
  const [busy, setBusy] = useState(false);
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    getSettings()
      .then(setTree)
      .catch(() => toast.error("Could not load settings"));
  }, []);

  const dirtyCount = Object.keys(pending).length;

  const stage = (key, value) =>
    setPending((p) => ({ ...p, [key]: value }));

  const stageMany = (entries) =>
    setPending((p) => ({ ...p, ...entries }));

  const onApply = async () => {
    if (dirtyCount === 0) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      const fn = gameId ? patchGameSettings : patchSettings;
      const args = gameId ? [gameId, pending] : [pending];
      const res = await fn(...args);
      setWarnings(res?.warnings || []);
      toast.success(`Applied ${res.applied?.length ?? 0} setting changes`);
      setPending({});
      setTree(res?.settings || null);
      onApplied?.(res?.settings);
      if (!res?.warnings?.length) onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not apply settings";
      if (e?.response?.status === 409) {
        toast.error(
          "Settings are locked — wait until the current round ends.",
        );
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  if (!tree) return null;

  return (
    <div
      data-testid="settings-panel"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/85 backdrop-blur-sm overflow-y-auto py-8 px-4"
    >
      <div className="w-full max-w-4xl bg-[color:var(--card)] rounded-[10px] shadow-[var(--shadow-card)] p-7">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex-1">
            <div className="label-kicker text-[color:var(--ink-3)]">Host settings</div>
            <h2 className="font-display text-3xl font-bold tracking-tight text-[color:var(--ink)]">
              House Rules
            </h2>
            <div className="text-xs text-[color:var(--ink-3)] mt-1" data-testid="settings-mode-note">
              {readOnly
                ? "View only in online games."
                : "Changes apply immediately when allowed. Mid-turn edits are blocked — open this between rounds for live changes."}
            </div>
          </div>
          <button
            data-testid="settings-close-btn"
            onClick={onClose}
            className="btn-tactile text-xs"
          >
            Close
          </button>
        </div>

        {warnings.length > 0 && (
          <div
            data-testid="settings-warnings"
            className="mb-4 border border-[color:var(--danger)] bg-[color:var(--surface)] p-3"
          >
            <div className="label-kicker text-[color:var(--danger)] mb-1">
              Balance warnings
            </div>
            <ul className="text-sm text-[color:var(--ink)] list-disc pl-5 space-y-0.5">
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}

        {showTesting && <TestingTools gameId={gameId} gameTestingMode={gameTestingMode} forcedWeather={gameForcedWeather} onSkipped={onApplied} onError={onError} maxRounds={tree?.economy?.endgame?.max_rounds ?? 12} />}

        <fieldset disabled={readOnly} data-testid="settings-fields" className="m-0 p-0 border-0 min-w-0">
        <Section title="Setup & economy" testid="settings-group-setup">
          <NumberRow
            label="Starting money"
            path="economy.starting_money"
            min={0} max={20} tree={tree} pending={pending} stage={stage}
          />
          <NumberRow
            label="Starting House Cubes per player · 2 players"
            path="foundation.starting_cubes_2p"
            min={1} max={8} tree={tree} pending={pending} stage={stage}
          />
          <NumberRow
            label="Starting House Cubes per player · 3 players"
            path="foundation.starting_cubes_3p"
            min={1} max={8} tree={tree} pending={pending} stage={stage}
          />
          <NumberRow
            label="Starting House Cubes per player · 4 players"
            path="foundation.starting_cubes_4p"
            min={1} max={8} tree={tree} pending={pending} stage={stage}
          />
          {[["small", "Small"], ["medium", "Medium"], ["big", "Big"]].map(([key, name]) => (
            <div key={key} data-testid={`settings-bracket-${key}`} className="contents">
              {key !== "small" && (
                <NumberRow
                  label={`${name} city · from population`}
                  path={`economy.city_brackets.${key}.min`}
                  min={1} max={60} tree={tree} pending={pending} stage={stage}
                />
              )}
              <NumberRow
                label={`${name} city · income per round`}
                path={`economy.city_brackets.${key}.income`}
                min={0} max={20} tree={tree} pending={pending} stage={stage}
              />
              <NumberRow
                label={`${name} city · repairs multiplier`}
                path={`economy.city_brackets.${key}.repair_multiplier`}
                min={0} max={5} tree={tree} pending={pending} stage={stage}
              />
            </div>
          ))}
          <NumberRow
            label="Coastal population bonus (per House Cube on a coastal square)"
            path="economy.coastal_population_bonus"
            min={0} max={5} tree={tree} pending={pending} stage={stage}
          />
          <NumberRow
            label="Urban Park population bonus (once per park next to your building)"
            path="economy.urban_park_population_bonus"
            min={0} max={5} tree={tree} pending={pending} stage={stage}
          />
        </Section>

        <Section title="Turn" testid="settings-group-turn">
          <NumberRow
            label="Build hand size"
            path="foundation.hand_size"
            min={1} max={8} tree={tree} pending={pending} stage={stage}
          />
          <NumberRow
            label="Action cards held (max)"
            path="foundation.action_cards_max"
            min={0} max={4} tree={tree} pending={pending} stage={stage}
          />
          <NumberRow
            label="Discards per turn"
            path="foundation.discards_per_turn"
            min={0} max={4} tree={tree} pending={pending} stage={stage}
          />
          <NumberRow
            label="Action card discards per player per game"
            path="foundation.action_discards_per_game"
            min={0} max={10} tree={tree} pending={pending} stage={stage}
          />
          <NumberRow
            label="Market wipe cost"
            path="foundation.market_wipe_cost"
            min={0} max={10} tree={tree} pending={pending} stage={stage}
          />
          <NumberRow
            label="Auto-wipe when this many market cards match"
            path="foundation.auto_wipe_same"
            min={2} max={5} tree={tree} pending={pending} stage={stage}
          />
          <NumberRow
            label="Demolitions per turn"
            path="foundation.demolitions_per_turn"
            min={0} max={5} tree={tree} pending={pending} stage={stage}
          />
          <NumberRow
            label="Demolish fee"
            path="board_mechanics.demolition_rules.demolish_fee"
            min={0} max={10} tree={tree} pending={pending} stage={stage}
          />
          <NumberRow
            label="Repair Card · uses per player per round"
            path="action_cards.cards.repair_card.max_per_round_per_player"
            min={1} max={5} tree={tree} pending={pending} stage={stage}
          />
        </Section>

        <Section title="Deck" testid="settings-group-deck">
          <p className="text-[11px] text-[color:var(--ink-3)] mb-2">
            Copies of each card per player count. Toggle a card off to
            remove it from the deck. Changes take effect on the next game
            start (or next round, if updated mid-game between rounds).
          </p>
          <DeckEditor
            tree={tree}
            pending={pending}
            stage={stage}
            stageMany={stageMany}
          />
        </Section>

        <Section title="Card costs" testid="settings-group-costs">
          <p className="text-[11px] text-[color:var(--ink-3)] mb-2">
            Money paid when a card is taken (market card, blind draw; every action card costs 3). Playing is free.
            Changes take effect on the next card played.
          </p>
          <CardCostList tree={tree} pending={pending} stage={stage} />
        </Section>

        <Section title="Card stats" testid="settings-group-stats">
          <p className="text-[11px] text-[color:var(--ink-3)] mb-2">
            Four stats per build card (0–3): Absorption, Shading, Storm
            protection, Population. Absorption, Shading and
            Storm protection feed the weather live.
          </p>
          <CardStatsList tree={tree} pending={pending} stage={stage} />
        </Section>

        <Section title="Board" testid="settings-group-board">
          <NumberRow
            label="Max floors per building (every square)"
            path="board_mechanics.cube_rules.max_floors"
            min={1} max={8} tree={tree} pending={pending} stage={stage}
          />
          <NumberRow
            label="Green Roofs per stack"
            path="board_mechanics.token_rules.green_roofs_per_stack"
            min={0} max={1} tree={tree} pending={pending} stage={stage}
          />
          <NumberRow
            label="Green Streets per square"
            path="board_mechanics.token_rules.green_streets_per_square"
            min={0} max={1} tree={tree} pending={pending} stage={stage}
          />
        </Section>

        <Section title="Weather" testid="settings-group-weather">
          <p className="text-[11px] text-[color:var(--ink-3)] mb-2">
            The app draws one weather card at the end of every round (default 20 cards: Clear Skies 4,
            Light Rain 3, Heavy Rain 5, Heat Wave 3, Heat Storm 2, Storm Surge 3). A drawn card is
            discarded; the discard is reshuffled when the deck is empty. When the meter reaches 1° or 2°
            the cards listed below leave the deck for good. The Protection Card cost is set under Card costs.
            The deck counts apply when a new game deals its deck; the removals are read live, at the moment the meter reaches a degree.
          </p>
          {WEATHER_FACES.map(([k, name]) => (
            <NumberRow
              key={k}
              label={`${name} · cards in the deck`}
              path={`weather.event.deck.${k}`}
              min={0} max={12} tree={tree} pending={pending} stage={stage}
            />
          ))}
          <DeckTotal tree={tree} pending={pending} />
          {[1, 2].map((deg) => (
            <div key={deg} className="flex flex-col gap-2" data-testid={`settings-removals-${deg}`}>
              <div className="text-[11px] font-semibold text-[color:var(--ink-2)]">{`At ${deg}° these cards leave the deck`}</div>
              {WEATHER_FACES.map(([k, name]) => (
                <NumberRow
                  key={k}
                  label={`${name} · removed at ${deg}°`}
                  path={`weather.event.removals.${deg}.${k}`}
                  min={0} max={12} tree={tree} pending={pending} stage={stage}
                />
              ))}
            </div>
          ))}
          {[
            ["light_rain", "Light Rain"],
            ["heavy_rain", "Heavy Rain"],
            ["heat_wave", "Heat Wave"],
            ["heat_storm", "Heat Storm"],
          ].map(([k, name]) => (
            <div key={k} className="flex flex-col gap-2">
              <NumberRow
                label={`${name} · shape size (connected squares)`}
                path={`weather.event.shape_size.${k}`}
                min={1} max={12} tree={tree} pending={pending} stage={stage}
              />
              <NumberRow
                label={`${name} · need per square`}
                path={`weather.event.need_per_square.${k}`}
                min={0} max={6} tree={tree} pending={pending} stage={stage}
              />
            </div>
          ))}
          <NumberRow
            label="Storm Surge · sea levels above the permanent sea"
            path="weather.event.surge.extra_level"
            min={0} max={3} tree={tree} pending={pending} stage={stage}
          />
          <NumberRow
            label="Storm Surge · repairs per square, minus its storm protection"
            path="weather.event.surge.cost_per_square"
            min={0} max={6} tree={tree} pending={pending} stage={stage}
          />
          <NumberRow
            label="Spare stat pays (max per player per event)"
            path="weather.event.surplus_pay_cap"
            min={0} max={10} tree={tree} pending={pending} stage={stage}
          />
          <NumberRow
            label="Protection Card time limit (s)"
            path="weather.event.protection_time_seconds"
            min={5} max={120} tree={tree} pending={pending} stage={stage}
          />
        </Section>

        <Section title="Carbon meter and sea level rise" testid="settings-group-sea">
          <p className="text-[11px] text-[color:var(--ink-3)] mb-2">
            The sea rises when the meter reaches 1, 2 and 3 degrees. A game keeps the number it started with.
          </p>
          <NumberRow label="Carbon per degree · 2 players" path="carbon.per_degree.2" min={5} max={30} tree={tree} pending={pending} stage={stage} />
          <NumberRow label="Carbon per degree · 3 players" path="carbon.per_degree.3" min={5} max={30} tree={tree} pending={pending} stage={stage} />
          <NumberRow label="Carbon per degree · 4 players" path="carbon.per_degree.4" min={5} max={30} tree={tree} pending={pending} stage={stage} />
          <NumberRow label="World emissions per round" path="carbon.world_per_round" min={0} max={5} tree={tree} pending={pending} stage={stage} />
        </Section>

        <Section title="Scoring" testid="settings-group-scoring">
          <p className="text-[11px] text-[color:var(--ink-3)] mb-2">
            After 12 rounds: score = population × weight + resilience × weight + debt × penalty. At 3°: resilience only. Money is not scored.
          </p>
          <NumberRow
            label="Population weight"
            path="scoring.population_weight"
            min={0} max={10} tree={tree} pending={pending} stage={stage}
          />
          <NumberRow
            label="Resilience weight"
            path="scoring.resilience_weight"
            min={0} max={10} tree={tree} pending={pending} stage={stage}
          />
          <NumberRow
            label="Resilience per grey building (negative)"
            path="scoring.grey_building_penalty"
            min={-10} max={0} tree={tree} pending={pending} stage={stage}
          />
          <NumberRow
            label="Debt penalty per unit of debt"
            path="scoring.debt_penalty"
            min={-10} max={0} tree={tree} pending={pending} stage={stage}
          />
          <TiebreakRow tree={tree} pending={pending} stage={stage} />
        </Section>

        <Section title="Game end" testid="settings-group-end">
          <NumberRow
            label="Max rounds"
            path="economy.endgame.max_rounds"
            min={1} max={30} tree={tree} pending={pending} stage={stage}
          />
        </Section>

        <Section title="Display (this screen only)" testid="settings-group-display">
          <p className="text-[11px] text-[color:var(--ink-3)] mb-2">
            Screen-only options, not rules. Editable at any time.
          </p>
          <ToggleRow
            label="Placement preview when hovering a square"
            path="display.placement_preview"
            tree={tree} pending={pending} stage={stage}
          />
          <ToggleRow
            label="Teaching line in the preview"
            path="display.teaching_line"
            tree={tree} pending={pending} stage={stage}
          />
          <ToggleRow
            label="Show all hands"
            path="display.show_all_hands"
            tree={tree} pending={pending} stage={stage}
          />
        </Section>
        </fieldset>

        <div className="mt-6 flex items-center justify-between gap-3">
          <div className="text-xs text-[color:var(--ink-3)]" data-testid="dirty-count">
            {dirtyCount > 0
              ? `${dirtyCount} pending change${dirtyCount > 1 ? "s" : ""}`
              : "No changes"}
          </div>
          <div className="flex gap-2">
            <button
              data-testid="settings-cancel-btn"
              onClick={onClose}
              className="btn-tactile text-xs"
              disabled={busy}
            >
              {readOnly ? "Close" : "Cancel"}
            </button>
            {!readOnly && (
            <button
              data-testid="settings-apply-btn"
              onClick={onApply}
              className="btn-tactile btn-tactile--primary"
              disabled={busy || dirtyCount === 0}
            >
              Apply ({dirtyCount})
            </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <section className="mt-5" data-testid={testid}>
      <h3 className="label-kicker text-[color:var(--ink)] mb-2 border-b border-[color:var(--line)] pb-1">
        {title}
      </h3>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

/**
 * TestingTools — sandbox section for developers / balance testing.
 *
 * Locked behind a "Testing Mode" toggle so casual games never see the
 * skip button. The toggle preference lives in localStorage.
 * Requires an active game (gameId != null) to actually run.
 */
function TestingTools({ gameId, gameTestingMode = false, forcedWeather = null, onSkipped, onError, maxRounds = 12 }) {
  const STORAGE_KEY = "porosity.testing_mode";
  const [enabled, setEnabled] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [target, setTarget] = useState(5);
  const [busy, setBusy] = useState(false);

  // The game itself must be in Testing Mode for test-only tools (forced weather).
  const syncGame = async (v) => {
    if (!gameId || v === gameTestingMode) return;
    try {
      await setTestingMode(gameId, v);
      onSkipped?.();
    } catch (e) {
      if (onError) onError(e, "Could not change Testing Mode");
      else toast.error(e?.response?.data?.detail || "Could not change Testing Mode");
    }
  };

  const persist = (v) => {
    setEnabled(v);
    try {
      localStorage.setItem(STORAGE_KEY, v ? "1" : "0");
    } catch {
      /* ignore */
    }
    syncGame(v);
  };

  // Testing Mode remembered from an earlier game: switch this game on too.
  useEffect(() => {
    if (enabled && gameId && !gameTestingMode) syncGame(true);
  }, []);

  const runSkip = async () => {
    if (!gameId) {
      toast.error("Skip to Round needs an active game session");
      return;
    }
    if (target < 1 || target > maxRounds) {
      toast.error(`Round must be between 1 and ${maxRounds}`);
      return;
    }
    try {
      setBusy(true);
      await skipToRound(gameId, target);
      toast.success(`Board seeded — jumped to Round ${target}`);
      onSkipped?.();
    } catch (e) {
      if (onError) onError(e, "Skip to Round failed");
      else toast.error(e?.response?.data?.detail || "Skip to Round failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="testing-tools-section"
      className="mt-3"
      style={{
        border: "2px dashed var(--danger)",
        background: "var(--surface)",
        borderRadius: 8,
        padding: 14,
      }}
    >
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div
            className="label-kicker"
            style={{ color: "var(--danger)" }}
            data-testid="testing-tools-kicker"
          >
            Testing Tools
          </div>
          <div className="text-xs text-[color:var(--ink-3)] mt-1">
            Sandbox utilities for balance & QA. Not for real sessions.
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-[color:var(--ink)]">
          <span className="label-kicker">Testing Mode</span>
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            onClick={() => persist(!enabled)}
            data-testid="testing-mode-toggle"
            className={`relative inline-block h-5 w-10 border border-[color:var(--line)] transition-colors ${
              enabled ? "bg-[color:var(--sea)]" : "bg-white"
            }`}
          >
            <span
              className={`absolute top-0 h-3 w-4 border border-[color:var(--line)] bg-white transition-transform ${
                enabled ? "translate-x-4" : "translate-x-0"
              }`}
            />
          </button>
          <span
            className="label-kicker"
            data-testid="testing-mode-state"
            style={{
              minWidth: 22,
              color: enabled ? "var(--sea)" : "var(--ink-5)",
            }}
          >
            {enabled ? "ON" : "OFF"}
          </span>
        </label>
      </div>

      {enabled ? (
        <div
          data-testid="skip-to-round-body"
          className="mt-4 flex flex-wrap items-end gap-3"
        >
          <label className="flex flex-col gap-1 text-xs text-[color:var(--ink)]">
            <span className="label-kicker text-[color:var(--ink-3)]">Target round</span>
            <input
              type="number"
              min={1}
              max={maxRounds}
              value={target}
              onChange={(e) =>
                setTarget(Math.max(1, Math.min(maxRounds, parseInt(e.target.value, 10) || 1)))
              }
              disabled={busy}
              data-testid="skip-to-round-input"
              className="w-24 border border-[color:var(--line)] bg-white px-2 py-1 tabular-nums"
            />
          </label>
          <button
            type="button"
            onClick={runSkip}
            disabled={busy || !gameId}
            data-testid="skip-to-round-btn"
            className="btn-tactile btn-tactile--primary text-xs"
            title={gameId ? "" : "Open this panel from an active game"}
          >
            {busy ? "Generating…" : "Generate Board State"}
          </button>
          <div className="text-[11px] text-[color:var(--ink-3)] leading-tight max-w-sm">
            Empties the board and seeds a realistic mid-game state:
            per-player pieces scale with the round, and a sea level rise
            has already happened from round 6.
          </div>
        </div>
      ) : null}
      {enabled && gameId ? <ForceWeather gameId={gameId} forced={forcedWeather} /> : null}
    </section>
  );
}

const WEATHER_FACES = [
  ["clear_skies", "Clear Skies"],
  ["light_rain", "Light Rain"],
  ["heavy_rain", "Heavy Rain"],
  ["heat_wave", "Heat Wave"],
  ["heat_storm", "Heat Storm"],
  ["storm_surge", "Storm Surge"],
];
const FORCE_FACES = WEATHER_FACES;

// Weather deck: live total of the (staged) card counts.
function DeckTotal({ tree, pending }) {
  const total = WEATHER_FACES.reduce((n, [k]) => n + (Number(currentValue(tree, pending, `weather.event.deck.${k}`)) || 0), 0);
  const bad = total < 1 || total > 40;
  return (
    <div data-testid="settings-deck-total" className="text-[11px] tabular-nums" style={{ color: bad ? "var(--danger)" : "var(--ink-3)" }}>
      Weather deck: {total} card{total === 1 ? "" : "s"}{bad ? " — needs 1 to 40" : ""}
    </div>
  );
}

// Testing Mode: choose the weather card (and optionally the shape's squares and the global card) of the next draw.
// Squares: "row,col" keys separated by spaces or ";" (e.g. "0,0 0,1 1,1 1,2"); blank = random shape.
const parseCells = (txt) => (txt || "").split(/[;\s]+/).map((t) => t.trim()).filter((t) => /^\d+,\d+$/.test(t));
// The choice is stored on the server (game.forced_weather) and used once by the automatic draw.
function ForceWeather({ gameId, forced = null }) {
  const [saved, setSaved] = useState(forced);
  const [face, setFace] = useState(saved?.face === "sunny_day" ? "clear_skies" : saved?.face || "light_rain");
  const [cellsText, setCellsText] = useState((saved?.cells || []).join(" "));
  const [globalNo, setGlobalNo] = useState(saved?.global_no ? String(saved.global_no) : "");
  const save = () => {
    const f = { face };
    const cells = parseCells(cellsText);
    if (cells.length) f.cells = cells;
    if (globalNo) f.global_no = Number(globalNo);
    weatherNextSpin(gameId, f)
      .then(() => {
        setSaved(f);
        toast.success(`Next weather card: ${FORCE_FACES.find(([k]) => k === face)?.[1]}`);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not set the next draw"));
  };
  const clear = () => {
    weatherNextSpin(gameId, null).catch(() => {});
    setSaved(null);
  };
  return (
    <div data-testid="force-weather-body" className="mt-4 flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1 text-xs text-[color:var(--ink)]">
        <span className="label-kicker text-[color:var(--ink-3)]">Force next weather</span>
        <select value={face} onChange={(e) => setFace(e.target.value)} data-testid="force-weather-face" className="border border-[color:var(--line)] bg-white px-2 py-1">
          {FORCE_FACES.map(([k, n]) => (
            <option key={k} value={k}>{n}</option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs text-[color:var(--ink)]">
        <span className="label-kicker text-[color:var(--ink-3)]">Shape squares (row,col)</span>
        <input type="text" value={cellsText} placeholder="random, e.g. 0,0 0,1 1,1 1,2" onChange={(e) => setCellsText(e.target.value)} data-testid="force-weather-cells" className="w-56 border border-[color:var(--line)] bg-white px-2 py-1 tabular-nums" />
      </label>
      <label className="flex flex-col gap-1 text-xs text-[color:var(--ink)]">
        <span className="label-kicker text-[color:var(--ink-3)]">Global card (1-24, blank = random)</span>
        <input type="number" min={1} max={24} value={globalNo} onChange={(e) => setGlobalNo(e.target.value)} data-testid="force-global-no" className="w-24 border border-[color:var(--line)] bg-white px-2 py-1 tabular-nums" />
      </label>
      <button type="button" onClick={save} data-testid="force-weather-save" className="btn-tactile btn-tactile--primary text-xs">
        Use on next draw
      </button>
      {saved && (
        <button type="button" onClick={clear} data-testid="force-weather-clear" className="btn-tactile text-xs">
          Clear
        </button>
      )}
      <div className="text-[11px] text-[color:var(--ink-3)] leading-tight max-w-sm" data-testid="force-weather-state">
        {saved
          ? `Queued: ${FORCE_FACES.find(([k]) => k === (saved.face === "sunny_day" ? "clear_skies" : saved.face === "extreme_heat_wave" ? "heat_storm" : saved.face))?.[1]}${saved.cells?.length ? ` on ${saved.cells.join(" ")}` : ""}${saved.global_no ? `, global card ${saved.global_no}` : ""} — used once by the next draw at the end of the round.`
          : "Nothing queued — the next draw is random. Shape squares must be connected land squares (blank = random shape; ignored for Storm Surge / Clear Skies)."}
      </div>
    </div>
  );
}

function readPath(tree, path) {
  return path.split(".").reduce(
    (acc, k) => (acc != null && k in acc ? acc[k] : undefined),
    tree,
  );
}

function currentValue(tree, pending, path) {
  return path in pending ? pending[path] : readPath(tree, path);
}

function NumberRow({ label, path, min, max, tree, pending, stage }) {
  const value = currentValue(tree, pending, path);
  const dirty = path in pending;
  return (
    <label
      className="flex items-center gap-3 text-sm"
      data-testid={`row-${path}`}
    >
      <span className="flex-1 text-[color:var(--ink)]">{label}</span>
      {dirty && (
        <span className="label-kicker text-[color:var(--danger)]">edited</span>
      )}
      <input
        type="number"
        min={min}
        max={max}
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => stage(path, parseInt(e.target.value, 10) || 0)}
        className="w-24 border border-[color:var(--line)] bg-white px-2 py-1 tabular-nums"
        data-testid={`input-${path}`}
      />
    </label>
  );
}

// Ordered list of every card key the House Rules should surface.
const BUILD_KEYS = [
  "house_cube", "urban_park", "urban_forest",
  "inland_wetland", "mangrove_forest", "green_roof", "green_street",
  "low_sea_wall",
];
const ACTION_KEYS = [
  "sabotage", "board_thief", "relocated",
  "protection_card", "repair_card",
];
const TYPE_LABEL = { building: "Building", nature: "Nature tile", upgrade: "Upgrade", action: "Action" };

function _rowByKey(tree, kind, key) {
  const rows = tree?.cards?.[kind] || [];
  return rows.find((r) => r.key === key);
}

function CardCostList({ tree, pending, stage }) {
  const rowFor = (kind, key) => _rowByKey(tree, kind, key);
  const render = (kind, keys) =>
    keys
      .map((key) => ({ key, row: rowFor(kind, key) }))
      .filter(({ row }) => !!row)
      .map(({ key, row }) => (
        <CostRow
          key={`${kind}.${key}`}
          label={row.name}
          path={`economy.card_cost_overrides.${key}`}
          defaultValue={row.cost}
          tree={tree}
          pending={pending}
          stage={stage}
        />
      ));
  return (
    <div className="space-y-2">
      <div className="label-kicker text-[color:var(--ink-3)]">Build cards</div>
      {render("build", BUILD_KEYS)}
      <div className="label-kicker text-[color:var(--ink-3)] mt-3">Action cards</div>
      {render("action", ACTION_KEYS)}
    </div>
  );
}

function CostRow({ label, path, defaultValue, tree, pending, stage }) {
  const staged = path in pending;
  const value = staged ? pending[path] : defaultValue;
  return (
    <label
      className="flex items-center gap-3 text-sm"
      data-testid={`row-${path}`}
    >
      <span className="flex-1 text-[color:var(--ink)]">{label}</span>
      {staged && <span className="label-kicker text-[color:var(--danger)]">edited</span>}
      <input
        type="number"
        min={0}
        max={20}
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) =>
          stage(path, Math.max(0, parseInt(e.target.value, 10) || 0))
        }
        className="w-24 border border-[color:var(--line)] bg-white px-2 py-1 tabular-nums"
        data-testid={`input-${path}`}
      />
    </label>
  );
}

function CardStatsList({ tree, pending, stage }) {
  const rowFor = (kind, key) => _rowByKey(tree, kind, key);
  const render = (kind, keys) =>
    keys
      .map((key) => ({ key, row: rowFor(kind, key) }))
      .filter(({ row }) => !!row)
      .map(({ key, row }) => (
        <StatsRow
          key={`stats.${kind}.${key}`}
          name={row.name}
          cardKey={key}
          defaults={row.stats || {}}
          pending={pending}
          stage={stage}
        />
      ));
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[1fr_repeat(4,44px)] gap-1.5 items-end text-[9px] uppercase tracking-wider text-[color:var(--ink-3)] pb-1 border-b border-[color:var(--line)]/40">
        <span>Card</span>
        {STAT_META.map((m) => (
          <span key={m.key} className="flex flex-col items-center gap-0.5 text-center leading-tight" title={m.label} data-testid={`stats-head-${m.key}`}>
            <m.Icon s={13} c={m.color} />
            {{ absorb: "Absorb", shade: "Shade", storm: "Storm", pop: "Pop." }[m.key]}
          </span>
        ))}
      </div>
      <div className="label-kicker text-[color:var(--ink-3)] mt-1">Build cards</div>
      {render("build", BUILD_KEYS)}
    </div>
  );
}

function StatsRow({ name, cardKey, defaults, pending, stage }) {
  const stats = STAT_META.map((m) => m.key);
  const stagedAny = stats.some(
    (s) => `economy.card_stat_overrides.${cardKey}.${s}` in pending,
  );
  return (
    <div
      className="grid grid-cols-[1fr_repeat(4,44px)] gap-1.5 items-center text-sm"
      data-testid={`stats-row-${cardKey}`}
    >
      <span className="text-[color:var(--ink)] flex items-center gap-2">
        {name}
        {stagedAny && (
          <span className="label-kicker text-[color:var(--danger)]">edited</span>
        )}
      </span>
      {stats.map((s) => {
        const path = `economy.card_stat_overrides.${cardKey}.${s}`;
        const value = path in pending ? pending[path] : (defaults[s] || 0);
        return (
          <input
            key={s}
            type="number"
            min={0}
            max={3}
            value={Number.isFinite(value) ? value : 0}
            onChange={(e) =>
              stage(path, Math.min(3, Math.max(0, parseInt(e.target.value, 10) || 0)))
            }
            className="w-full border border-[color:var(--line)] bg-white px-1 py-1 tabular-nums text-center"
            data-testid={`input-${path}`}
          />
        );
      })}
    </div>
  );
}


function ToggleRow({ label, path, tree, pending, stage }) {
  const value = !!currentValue(tree, pending, path);
  return (
    <label
      className="flex items-center justify-between gap-3 text-sm"
      data-testid={`row-${path}`}
    >
      <span className="text-[color:var(--ink)]">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => stage(path, !value)}
        data-testid={`toggle-${path}`}
        className={`relative h-6 w-12 border border-[color:var(--line)] transition-colors ${
          value ? "bg-[color:var(--sea)]" : "bg-white"
        }`}
      >
        <span
          className={`absolute top-0.5 h-3.5 w-5 border border-[color:var(--line)] bg-white transition-transform ${
            value ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </button>
    </label>
  );
}

// Tiebreaks: after 12 rounds (resilience, then fewest repairs paid) and at 3° (population, then fewest repairs paid).
// Each ending is staged on its own path: scoring.tiebreaker_order.full / .early.
function TiebreakRow({ tree, pending, stage }) {
  const rows = [
    { mode: "full", lead: "resilience", label: "12 rounds · tie: resilience, then fewest repairs paid" },
    { mode: "early", lead: "population", label: "3° · tie: population, then fewest repairs paid" },
  ];
  return (
    <>
      {rows.map(({ mode, lead, label }) => {
        const path = `scoring.tiebreaker_order.${mode}`;
        const cur = currentValue(tree, pending, path);
        const order = Array.isArray(cur) ? cur : [];
        const on = order.includes("fewest_flood_paid");
        return (
          <label key={mode} className="flex items-center justify-between gap-3 text-sm" data-testid={`row-${path}`}>
            <span className="text-[color:var(--ink)]">{label}</span>
            <button
              type="button"
              role="switch"
              aria-checked={on}
              onClick={() => stage(path, on ? [lead] : [lead, "fewest_flood_paid"])}
              data-testid={`toggle-${path}`}
              className={`relative h-6 w-12 border border-[color:var(--line)] transition-colors ${
                on ? "bg-[color:var(--sea)]" : "bg-white"
              }`}
            >
              <span
                className={`absolute top-0.5 h-3.5 w-5 border border-[color:var(--line)] bg-white transition-transform ${
                  on ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </label>
        );
      })}
    </>
  );
}

// Player counts with their own deck column (shown 4 → 3 → 2).
const DECK_PCS = [4, 3, 2];

function DeckEditor({ tree, pending, stage, stageMany }) {
  const buildRows = tree.cards.build;
  const actionRows = tree.cards.action;

  // Effective copies for a row, taking pending edits into account.
  const effective = (row, pc) => {
    const k = `cards.${row.kind}.${row.key}.copies_${pc}p`;
    if (k in pending) return pending[k];
    return row[`copies_${pc}p`] ?? 0;
  };

  const total = (rows, pc) =>
    rows.reduce((sum, r) => sum + (effective(r, pc) || 0), 0);

  const onResetDefaults = () => {
    const next = {};
    for (const r of [...buildRows, ...actionRows]) {
      for (const pc of DECK_PCS) next[`cards.${r.kind}.${r.key}.copies_${pc}p`] = r[`default_${pc}p`] ?? 0;
    }
    stageMany(next);
  };

  return (
    <div data-testid="deck-editor" className="space-y-4">
      <div className="flex items-center justify-between gap-3 text-xs">
        <div className="flex flex-wrap gap-3">
          {DECK_PCS.map((pc) => (
            <TotalsChip
              key={pc}
              label={`Total · ${pc} players`}
              value={total(buildRows, pc) + total(actionRows, pc)}
              testid={`deck-total-${pc}p`}
            />
          ))}
        </div>
        <button
          type="button"
          data-testid="deck-reset-btn"
          onClick={onResetDefaults}
          className="btn-tactile text-xs"
        >
          Reset to defaults
        </button>
      </div>

      <DeckTable
        title="Build cards"
        rows={buildRows}
        pending={pending}
        stage={stage}
        effective={effective}
      />
      <DeckTable
        title="Action cards"
        rows={actionRows}
        pending={pending}
        stage={stage}
        effective={effective}
      />
    </div>
  );
}

function TotalsChip({ label, value, testid }) {
  return (
    <div
      className="border border-[color:var(--line)] bg-white px-3 py-1.5 tabular-nums"
      data-testid={testid}
    >
      <span className="label-kicker text-[color:var(--ink-3)] mr-2">{label}</span>
      <span className="text-[color:var(--ink)] font-bold">{value}</span>
    </div>
  );
}

function DeckTable({ title, rows, pending, stage, effective }) {
  return (
    <div className="border border-[color:var(--line)] bg-white">
      <div className="border-b border-[color:var(--line)] bg-[color:var(--line-soft)] px-3 py-1.5 label-kicker text-[color:var(--ink)]">
        {title}
      </div>
      <table className="w-full text-xs" data-testid={`deck-table-${title.split(" ")[0].toLowerCase()}`}>
        <thead>
          <tr className="border-b border-[color:var(--line)] bg-[color:var(--surface)] text-left text-[10px] uppercase tracking-wider">
            <th className="px-2 py-1.5">Card</th>
            <th className="px-2 py-1.5">Type</th>
            <th className="px-2 py-1.5 text-center">In deck</th>
            {DECK_PCS.map((pc) => (
              <th key={pc} className="px-2 py-1.5 text-center">{`${pc}-player`}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <DeckRow
              key={row.key}
              row={row}
              pending={pending}
              stage={stage}
              effective={effective}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DeckRow({ row, pending, stage, effective }) {
  const keyFor = (pc) => `cards.${row.kind}.${row.key}.copies_${pc}p`;
  const copies = Object.fromEntries(DECK_PCS.map((pc) => [pc, effective(row, pc)]));
  const enabled = DECK_PCS.some((pc) => copies[pc] > 0);
  const dirty = DECK_PCS.some((pc) => keyFor(pc) in pending);

  // Toggle: when ON, restore defaults if currently all 0; when OFF, set every count to 0.
  const onToggle = () => {
    for (const pc of DECK_PCS) stage(keyFor(pc), enabled ? 0 : (row[`default_${pc}p`] || 1));
  };

  const cat = TYPE_LABEL[row.card_type] || (row.kind === "action" ? "Action" : "—");
  const placementLines = (row.placement_text || "").split("\n").filter(Boolean);

  return (
    <tr
      className={`border-t border-[color:var(--line)]/30 ${dirty ? "bg-[color:var(--surface)]" : ""}`}
      data-testid={`deck-row-${row.key}`}
    >
      <td className="px-2 py-1.5">
        <div className="flex items-center gap-2">
          <span className={`font-medium ${!enabled ? "text-[color:var(--ink-5)] line-through" : "text-[color:var(--ink)]"}`}>
            {row.name}
          </span>
          {dirty && (
            <span className="label-kicker text-[color:var(--danger)]">edited</span>
          )}
        </div>
        {row.description && (
          <div className="mt-0.5 text-[10.5px] leading-snug text-[color:var(--ink-3)]" data-testid={`deck-desc-${row.key}`}>
            {row.description}
          </div>
        )}
        {placementLines.map((l) => (
          <div key={l} className="mt-0.5 text-[10.5px] leading-snug font-semibold text-[color:var(--ink-2)]" data-testid={`deck-placement-${row.key}`}>
            {l}
          </div>
        ))}
      </td>
      <td className="px-2 py-1.5 text-[color:var(--ink-3)]">{cat}</td>
      <td className="px-2 py-1.5 text-center">
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={onToggle}
          data-testid={`deck-toggle-${row.key}`}
          title={enabled ? "Remove from deck" : "Include in deck"}
          className={`relative inline-block h-5 w-10 border border-[color:var(--line)] transition-colors ${
            enabled ? "bg-[color:var(--sea)]" : "bg-white"
          }`}
        >
          <span
            className={`absolute top-0 h-3 w-4 border border-[color:var(--line)] bg-white transition-transform ${
              enabled ? "translate-x-4" : "translate-x-0"
            }`}
          />
        </button>
      </td>
      {DECK_PCS.map((pc) => {
        const on = copies[pc] > 0;
        return (
          <td key={pc} className="px-2 py-1.5 text-center">
            <input
              type="number"
              min={0}
              max={50}
              value={copies[pc]}
              disabled={!on}
              onChange={(e) =>
                stage(keyFor(pc), Math.max(0, parseInt(e.target.value, 10) || 0))
              }
              className={`w-14 border border-[color:var(--line)] bg-white px-2 py-0.5 tabular-nums text-center ${
                !on ? "bg-[color:var(--line-soft)] text-[color:var(--ink-5)] cursor-not-allowed" : ""
              }`}
              data-testid={`deck-input-${pc}p-${row.key}`}
            />
          </td>
        );
      })}
    </tr>
  );
}
